import { useTranslation } from 'react-i18next';
import {
  QUERY_SEARCH_PARAM_CATALOG_AGRID,
  QUERY_SEARCH_PARAM_CATALOG_HAGRID,
  QUERY_SEARCH_PARAM_CATALOG_HWGID
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { useEffect, useMemo, useRef } from 'react';
import { HouseIcon } from 'lucide-react';
import type { BaseArticleDetail } from '~/common/types/baseArticleDetail';
import { useBasePath } from '~/common/hooks/useBasePath';
import { cn } from '~/common/utils/styleUtil';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import type { BreadcrumbItemInfo } from '~/common/components/BreadcrumbItemInfo.ts';
import {
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbItem,
  Breadcrumb
} from '~/common/components/ui/breadcrumb.tsx';
import { Link } from 'react-router';
import { BreadcrumbBackButton } from '~/common/components/BreadcrumbBackButton';
import type { TFunction } from 'i18next';

const TRANSLATION_KEYS = {
  HOME: 'Startseite',
  CATALOG: 'Gesamtsortiment'
};

export interface ArticleDetailBreadcrumbProps {
  article: BaseArticleDetail;
  className?: string;
}

const createBreadcrumbs = (
  t: TFunction, // ReturnType<typeof useTranslation>['t'],
  path: string,
  { hwgId, hwg, hagrId, hagr, agrId, agr }: BaseArticleDetail
): BreadcrumbItemInfo[] => {
  const linkSearchParams = new URLSearchParams();
  const items: BreadcrumbItemInfo[] = [
    { key: 'home', icon: <HouseIcon size={14} />, name: t(TRANSLATION_KEYS.HOME as any), link: `${path}` },
    { key: 'catalog', name: t(TRANSLATION_KEYS.CATALOG as any), link: `${path}/catalog` }
  ];

  if (hwgId && hwg) {
    linkSearchParams.set(QUERY_SEARCH_PARAM_CATALOG_HWGID, hwgId.toString());
    items.push({ key: `hwg-${hwgId}`, name: hwg, link: `${path}/catalog?${linkSearchParams}` });

    if (hagrId && hagr) {
      linkSearchParams.set(QUERY_SEARCH_PARAM_CATALOG_HAGRID, hagrId.toString());
      items.push({ key: `hagr-${hagrId}`, name: hagr, link: `${path}/catalog?${linkSearchParams}` });

      if (agrId && agr) {
        linkSearchParams.set(QUERY_SEARCH_PARAM_CATALOG_AGRID, agrId.toString());
        items.push({ key: `agr-${agrId}`, name: agr, link: `${path}/catalog?${linkSearchParams}` });
      }
    }
  }

  return items;
};

export function ArticleDetailBreadcrumb({ article, className }: Readonly<ArticleDetailBreadcrumbProps>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const { isMobile, screenSize } = useScreenSize();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const breadcrumbs = useMemo(() => createBreadcrumbs(t, path, article), [t, path, article]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollLeft = container.scrollWidth;
    }
  }, [breadcrumbs, screenSize.width]);

  return (
    <div className={cn('flex items-center gap-2 pb-4', className)}>
      <BreadcrumbBackButton />
      <div
        className="overflow-x-auto no-scrollbar"
        style={{ width: isMobile ? `${screenSize.width - 32}px` : 'auto' }}
        ref={scrollContainerRef}
      >
        <Breadcrumb>
        <BreadcrumbList className="flex-nowrap">
          {breadcrumbs.map((item, index) => (
            <div key={item.key} className="flex items-center">
              {index > 0 && <BreadcrumbSeparator className={'pr-1'} />}
              <BreadcrumbItem className="whitespace-nowrap">
                <BreadcrumbLink asChild>
                  <Link
                    to={item.link}
                    className="flex items-center gap-1 hover:text-primary text-base"
                    data-testid={`breadcrumb-${item.link}`}
                  >
                    {item.icon}
                    {item.name}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </div>
          ))}
        </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
}
